import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Button, InputAdornment, Link, MenuItem, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import { listFinanceInvoices } from '../../api/finance';
import { downloadInvoicePdf, getStudentInvoicePdfBlob } from '../../api/students';
import { useBranchFilter } from '../../context/branchContext';
import {
  DocumentPreviewDialog,
  DocumentPreviewTarget,
} from '../../components/DocumentPreviewDialog';
import { PageHeader } from '../../components/PageHeader';
import { TableToolbar } from '../../components/table/TableToolbar';
import { downloadBlob } from '../../utils/downloadBlob';
import { FinanceFilters, FinanceInvoiceRow } from '../../types/finance';
import { ExportButtons } from './ExportButtons';
import { FinanceFilterBar } from './FinanceFilterBar';
import { formatDate, formatMoney } from './financeFormat';

/** Finance → Invoices (Phase 96): every invoice issued to students in
 * scope. Invoices are immutable snapshots; PDFs come from the existing
 * per-student invoice endpoint. Generate new ones from a student's
 * Payment Checklist. */
export const FinanceInvoicesPage = () => {
  const { selectedBranchId } = useBranchFilter();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });
  const [search, setSearch] = useState('');
  const [invoiceType, setInvoiceType] = useState<'USD' | 'LKR' | ''>('');
  const [filters, setFilters] = useState<FinanceFilters>({});
  const [preview, setPreview] = useState<DocumentPreviewTarget | null>(null);
  const resetPage = () => setPaginationModel((p) => ({ ...p, page: 0 }));

  const params = {
    ...filters,
    branchId: selectedBranchId,
    search: search || undefined,
    invoiceType: invoiceType || undefined,
  };
  const listQuery = useQuery({
    queryKey: ['finance-invoices', paginationModel, params],
    queryFn: () =>
      listFinanceInvoices({
        ...params,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      }),
    placeholderData: (prev) => prev,
  });

  const download = async (row: FinanceInvoiceRow) =>
    downloadBlob(await downloadInvoicePdf(row.student.id, row.id), `${row.invoiceNumber}.pdf`);

  const columns: GridColDef<FinanceInvoiceRow>[] = [
    { field: 'invoiceNumber', headerName: 'Invoice', width: 115 },
    { field: 'issuedAt', headerName: 'Issued', width: 110, valueFormatter: (v) => formatDate(v) },
    { field: 'invoiceType', headerName: 'Type', width: 80, valueFormatter: (v) => v ?? '—' },
    {
      field: 'student',
      headerName: 'Student',
      flex: 1,
      minWidth: 170,
      sortable: false,
      renderCell: ({ row }) => (
        <Link component={RouterLink} to={`/finance/students/${row.student.id}`}>
          {row.student.name} ({row.student.studentNumber})
        </Link>
      ),
    },
    { field: 'branch', headerName: 'Branch', width: 110, valueFormatter: (v) => v ?? '—' },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (_v, row) => formatMoney(row.amount, row.currency),
    },
    {
      field: 'totalLkr',
      headerName: 'Total LKR',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v: number | null) =>
        v == null ? '—' : v.toLocaleString('en-US', { minimumFractionDigits: 2 }),
    },
    { field: 'paymentNumber', headerName: 'Payment', width: 110, valueFormatter: (v) => v ?? '—' },
    { field: 'issuedBy', headerName: 'Issued by', width: 140 },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Button
            size="small"
            startIcon={<DescriptionIcon />}
            onClick={() =>
              setPreview({
                key: `inv-${row.id}`,
                originalName: `${row.invoiceNumber}.pdf`,
                mimeType: 'application/pdf',
                fetchBlob: () => getStudentInvoicePdfBlob(row.student.id, row.id),
                onDownload: () => void download(row),
              })
            }
          >
            View
          </Button>
          <Button size="small" onClick={() => void download(row)}>
            PDF
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={DescriptionIcon}
        color="#00838F"
        title="Invoices"
        subtitle="USD and LKR invoices issued to students. Generate new invoices from a student's Payment Checklist."
        actions={<ExportButtons params={{ report: 'invoices', ...params }} />}
      />
      <FinanceFilterBar
        value={filters}
        onChange={(next) => {
          setFilters(next);
          resetPage();
        }}
      >
        <TextField
          placeholder="Search invoice or student…"
          size="small"
          sx={{ width: 230 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetPage();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Type"
          sx={{ minWidth: 110 }}
          value={invoiceType}
          onChange={(e) => {
            setInvoiceType(e.target.value as 'USD' | 'LKR' | '');
            resetPage();
          }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="USD">USD</MenuItem>
          <MenuItem value="LKR">LKR</MenuItem>
        </TextField>
      </FinanceFilterBar>
      {listQuery.isError ? (
        <Alert severity="error">Couldn't load invoices.</Alert>
      ) : (
        <Box sx={{ height: 640, bgcolor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={listQuery.data?.data ?? []}
            columns={columns}
            rowCount={listQuery.data?.meta.total ?? 0}
            loading={listQuery.isFetching}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[20, 50, 100]}
            slots={{ toolbar: TableToolbar }}
            localeText={{ noRowsLabel: 'No invoices match these filters.' }}
            disableRowSelectionOnClick
          />
        </Box>
      )}
      <DocumentPreviewDialog target={preview} onClose={() => setPreview(null)} />
    </Box>
  );
};
